import type {
  CareplanCartDataModel,
  CareplanProductDataModel,
  SubscriptionDataModel,
  SubscriptionProductDataModel,
} from "@/domains/careplan/models/types";
import { env } from "@/env";
import type { CareplanCartItem } from "@/stores";
import { formatAmount, roundToCents } from "@/utils/money";

export type CareplanOrderLine = {
  id: string;
  variantId: string | null;
  merchandiseId: string;
  sku: string | null;
  name: string;
  shortName: string;
  image: string;
  quantity: number;
  isActive: boolean;
  unitPrice: number;
  unitPriceAsString: string;
  unitListPrice: number;
  unitListPriceAsString: string;
  product: CareplanProductDataModel | null;
  subscriptionProduct: SubscriptionProductDataModel | null;
  isSubscribed: boolean;
};

type CreateCareplanOrderModelOptions = {
  cart: NonNullable<CareplanCartDataModel>;
  recommendedCartLines: CareplanCartItem[] | null;
  products: CareplanProductDataModel[];
  subscription?: SubscriptionDataModel | null;
};

export const getCareplanUnitPricing = (item: CareplanCartItem) => {
  const unitListPrice = Number.isFinite(item.unitListPrice)
    ? item.unitListPrice
    : Number(item.originalPriceAsString);

  const fallbackUnitPrice =
    item.quantity > 0 ? roundToCents(item.price / item.quantity) : unitListPrice;
  const unitPrice = Number.isFinite(item.unitPrice) ? item.unitPrice : fallbackUnitPrice;

  return {
    unitPrice,
    unitPriceAsString: formatAmount(unitPrice),
    unitListPrice,
    unitListPriceAsString: formatAmount(unitListPrice),
  };
};

export const createCareplanOrderModel = ({
  cart,
  products,
  recommendedCartLines,
  subscription = null,
}: CreateCareplanOrderModelOptions) => {
  const snapshotLines = recommendedCartLines?.length ? recommendedCartLines : cart.items;

  const liveLinesByVariantId = new Map(
    cart.items.map((item) => {
      return [item.variantId, item] as const;
    }),
  );

  const productsByVariantId = new Map(
    products.flatMap((product) => {
      return product.variantId ? [[product.variantId, product] as const] : [];
    }),
  );

  const subscriptionProductsByVariantId = new Map(
    (subscription?.products ?? []).map((product) => {
      return [product.variantId, product] as const;
    }),
  );

  const snapshotsByVariantId = new Map(
    snapshotLines.map((line) => {
      return [line.variantId, line] as const;
    }),
  );

  const orderedSnapshots = [
    ...products.flatMap((product) => {
      const snapshot = product.variantId ? snapshotsByVariantId.get(product.variantId) : undefined;

      return snapshot ? [snapshot] : [];
    }),
    ...snapshotLines.filter((line) => {
      return !line.variantId || !productsByVariantId.has(line.variantId);
    }),
  ];

  const lines = orderedSnapshots.map((snapshot): CareplanOrderLine => {
    const liveLine = snapshot.variantId ? liveLinesByVariantId.get(snapshot.variantId) : undefined;
    const product = snapshot.variantId
      ? (productsByVariantId.get(snapshot.variantId) ?? null)
      : null;
    const subscriptionProduct = snapshot.variantId
      ? (subscriptionProductsByVariantId.get(snapshot.variantId) ?? null)
      : null;

    const pricing = subscriptionProduct
      ? {
          unitPrice: subscriptionProduct.finalPrice,
          unitPriceAsString: formatAmount(subscriptionProduct.finalPrice),
          unitListPrice: subscriptionProduct.price,
          unitListPriceAsString: formatAmount(subscriptionProduct.price),
        }
      : getCareplanUnitPricing(liveLine ?? snapshot);
    const quantity = subscriptionProduct?.quantity ?? liveLine?.quantity ?? 0;

    return {
      id: liveLine?.id ?? snapshot.id,
      variantId: snapshot.variantId ?? null,
      merchandiseId: snapshot.merchandiseId,
      sku: liveLine?.sku ?? snapshot.sku ?? null,
      name: snapshot.name,
      shortName: snapshot.shortName,
      image: liveLine?.image ?? snapshot.image,
      quantity,
      isActive: quantity > 0,
      ...pricing,
      product,
      subscriptionProduct,
      isSubscribed: !!subscriptionProduct,
    };
  });

  const activeLines = lines.filter((line) => {
    return line.isActive;
  });

  const shippingCost = Number(cart.selectedShippingOption?.cost ?? 0);

  const subtotal = roundToCents(
    activeLines.reduce((total, line) => {
      return total + line.unitListPrice * line.quantity;
    }, 0),
  );

  const extraDiscounts = cart.discounts.filter((discount) => {
    return discount.code !== env.VITE_CAREPLAN_DISCOUNT_CODE;
  });

  const extraDiscountsTotal = extraDiscounts.reduce((sum, discount) => {
    return sum + discount.total;
  }, 0);

  const savings = roundToCents(
    Math.max(
      activeLines.reduce((total, line) => {
        return total + (line.unitListPrice - line.unitPrice) * line.quantity;
      }, 0),
      0,
    ),
  );

  const total = roundToCents(subtotal - savings - extraDiscountsTotal + shippingCost);

  const hasCareplanCode = cart.discountCodes.some((discountCode) => {
    return discountCode.applicable && discountCode.code === env.VITE_CAREPLAN_DISCOUNT_CODE;
  });

  return {
    cartId: cart.id,
    subscriptionStatus: subscription?.status ?? null,
    lines,
    activeLines,
    productCount: activeLines.length,
    unitCount: activeLines.reduce((count, line) => {
      return count + line.quantity;
    }, 0),
    isEmpty: activeLines.length === 0,
    subtotal,
    subtotalAsString: formatAmount(subtotal),
    savings,
    savingsAsString: formatAmount(savings),
    hasCareplanCode,
    total,
    totalAsString: formatAmount(total),
    shipping: cart.selectedShippingOption,
    extraDiscounts,
  };
};
